import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './Icons';
import { getInfoEmail } from '../utils/branding';
import { PILLARS } from '../data/catalog';

const MESSAGE_MAX_LENGTH = 1000;

const SERVICE_OPTIONS = [
  ...PILLARS.map((p) => p.title),
  'Internal Audit',
  'External Audit',
  'Financial Audit',
  'IT Audit & Vendor Governance',
  'Accounting & Bookkeeping',
  'Tax Consultancy',
  'Cost Control & Risk Management',
  'Accounting Training',
  'Business Analysis',
  'Trademark Registration',
  'Data Entry & Records Management',
  'Other',
];

const Contact = () => {
  const infoEmail = getInfoEmail();

  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', service: '', message: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ loading: false, success: false, error: false, message: '' });

  const validate = (data) => {
    const e = {};
    if (!data.name.trim()) e.name = 'Please enter your name';
    if (!data.email.trim()) e.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Please enter a valid email address';
    if (!data.message.trim()) e.message = 'Please enter a message';
    return e;
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    if (touched[name]) setErrors(validate(updated));
  };

  const handleBlur = (ev) => {
    const { name } = ev.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors(validate(formData));
  };

  const buildMailtoLink = () => {
    const subjectParts = [`New inquiry from ${formData.name}`];
    if (formData.service) subjectParts.push(formData.service);
    const bodyLines = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      formData.company && `Company: ${formData.company}`,
      formData.phone && `Phone: ${formData.phone}`,
      formData.service && `Service Interest: ${formData.service}`,
      '', 'Message:', formData.message,
    ].filter((l) => l !== '' && l !== undefined && l !== false);
    return `mailto:${infoEmail}?subject=${encodeURIComponent(subjectParts.join(' - '))}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    setTouched({ name: true, email: true, message: true });
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatus({ loading: false, success: false, error: true, message: 'Please fix the highlighted fields before sending.' });
      return;
    }
    setStatus({ loading: true, success: false, error: false, message: '' });
    window.location.href = buildMailtoLink();
    setTimeout(() => {
      setStatus({
        loading: false, success: true, error: false,
        message: 'Your email app should now be open with your message ready to go — just hit Send there to reach us.',
      });
    }, 500);
  };

  const inputClass = (f) => `form-input ${touched[f] && errors[f] ? 'form-input-error' : ''}`;

  return (
    <section className="cta-band" id="contact">
      <div className="wrap">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: 44 }}
        >
          <h2>Ready to scope your modernization program?</h2>
          <p>
            Share your current environment and goals &mdash; we&apos;ll map it to the six capabilities
            above and propose an engagement plan.
          </p>
        </motion.div>

        <div className="contact-grid" style={{ textAlign: 'left' }}>
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ color: '#C9D3DC' }}
          >
            <h3 style={{ color: '#fff' }}>Contact</h3>
            <div className="contact-line">
              <span className="ci-icon"><Icon name="mail" size={20} /></span>
              <div>
                <h4 style={{ color: '#fff' }}>Email</h4>
                <a href={`mailto:${infoEmail}`} style={{ color: '#C9D3DC' }}>{infoEmail}</a>
              </div>
            </div>
            <div className="hours-box" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.12)' }}>
              <h4 style={{ color: '#fff' }}>Business Hours</h4>
              <p style={{ color: '#AFC0CE' }}>Monday – Friday: 9:00 AM – 6:00 PM</p>
              <p style={{ color: '#AFC0CE' }}>Saturday: 10:00 AM – 4:00 PM</p>
              <p style={{ color: '#AFC0CE' }}>Sunday: Emergency Support Only</p>
            </div>
          </motion.div>

          <motion.div
            className="contact-card"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Send us a message</h3>

            <AnimatePresence>
              {status.success && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginBottom: 18 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  className="form-note ok"
                >
                  <Icon name="check" size={18} />
                  <span>{status.message}</span>
                </motion.div>
              )}
              {status.error && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginBottom: 18 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  className="form-note err"
                >
                  <Icon name="warn" size={18} />
                  <span>{status.message}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input id="name" name="name" type="text" value={formData.name}
                    onChange={handleChange} onBlur={handleBlur} className={inputClass('name')}
                    placeholder="Your full name" />
                  {touched.name && errors.name && <p className="form-error">{errors.name}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input id="email" name="email" type="email" value={formData.email}
                    onChange={handleChange} onBlur={handleBlur} className={inputClass('email')}
                    placeholder="your.email@company.com" />
                  {touched.email && errors.email && <p className="form-error">{errors.email}</p>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company</label>
                  <input id="company" name="company" type="text" value={formData.company}
                    onChange={handleChange} className="form-input" placeholder="Your organization" />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input id="phone" name="phone" type="tel" value={formData.phone}
                    onChange={handleChange} className="form-input" placeholder="+1 (555) 123-4567" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="service" className="form-label">Service Interest</label>
                <select id="service" name="service" value={formData.service}
                  onChange={handleChange} className="form-select">
                  <option value="">Select a capability</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label htmlFor="message" className="form-label">Message *</label>
                  <span style={{ fontSize: 12, color: '#98A2AD' }}>
                    {formData.message.length}/{MESSAGE_MAX_LENGTH}
                  </span>
                </div>
                <textarea id="message" name="message" rows="5" value={formData.message}
                  onChange={handleChange} onBlur={handleBlur} maxLength={MESSAGE_MAX_LENGTH}
                  className={`form-textarea ${touched.message && errors.message ? 'form-input-error' : ''}`}
                  placeholder="Tell us about your environment, goals and timelines..." />
                {touched.message && errors.message && <p className="form-error">{errors.message}</p>}
              </div>

              <motion.button
                type="submit"
                disabled={status.loading}
                whileHover={{ scale: status.loading ? 1 : 1.01 }}
                whileTap={{ scale: status.loading ? 1 : 0.98 }}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                {status.loading ? 'Opening your email app…' : 'Send Message'}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
